import { KeyboardEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import ReactApexChart from 'react-apexcharts'
import type { ApexOptions } from 'apexcharts'
import { UrgencyDailyPointDTO } from '../../../dto/urgencySummary/UrgencySummaryDTO'
import { useChartTokens } from '../../ServicesStatistics/chartTokens'
import { PeakAnalysis } from '../../ServicesStatistics/peaks'
import { dayPoint } from '../../ServicesStatistics/periodDetail'
import { escapeHtml, parseIsoDate } from '../../ServicesStatistics/statisticsUtils'
import { axisLabel, peakLabel, periodTitle } from '../../ServicesStatistics/TimelineChart'
import { addDays } from '../../../components/novarium/periodRange'
import { hasArrivals, UrgencyMeasure } from '../urgencyAnalysis'
import { formatCount, formatMinutes } from '../urgencySummaryFormat'

interface UrgencyTimelineChartProps {
  daily: UrgencyDailyPointDTO[]
  measure: UrgencyMeasure
  ariaLabel: string
  /** Promedio del período para la línea de referencia (llegadas por día o espera media); null: sin línea. */
  average: number | null
  /** Picos y días con pocos pacientes, uno por posición de `daily`; null con «Llegadas» (no hay picos). */
  analysis: PeakAnalysis | null
  /** Click, tap o Enter sobre un día con llegadas: abre su detalle. `opener` es a donde vuelve el foco al cerrarlo. */
  onOpen: (day: UrgencyDailyPointDTO, opener: HTMLElement | null) => void
}

const CHART_HEIGHT = 280
const HALF_DAY_MS = 12 * 60 * 60 * 1000
/** Marcador de un punto común, de un pico y del punto resaltado con el teclado. */
const MARKER_SIZE = 3
const PEAK_MARKER_SIZE = 7
const ACTIVE_MARKER_SIZE = 8
/** Trazo punteado de la serie «Se retiraron sin atención». */
const LEFT_DASH = 6
/** ApexCharts avisa del click por dos eventos (`markerClick` y `dataPointSelection`): el segundo, dentro de este tiempo, se ignora. */
const DUPLICATE_CLICK_MS = 400

/** Un punto del eje de tiempo: con `day` es un día dibujado; sin él, un extremo del rango que no tiene valor. */
interface PlotPoint {
  x: number
  y: number | null
  left: number | null
  day: UrgencyDailyPointDTO | null
  /** Posición del día en `daily` (y en `analysis`). */
  index: number
}

const dayStart = (day: UrgencyDailyPointDTO): Date => parseIsoDate(day.date)

/** Título completo del día: «Sábado 19/09/2026». */
const dayTitle = (intl: IntlShape, day: UrgencyDailyPointDTO): string =>
  periodTitle(intl, dayPoint(dayStart(day), day.arrivals, day.avgWaitMinutes, null), 'DAY')

/** El valor de la medida elegida en el día; null si no hay con qué calcularlo. */
const valueOf = (day: UrgencyDailyPointDTO, measure: UrgencyMeasure): number | null =>
  (measure === 'arrivals' ? day.arrivals : day.avgWaitMinutes)

interface PointFlags {
  peak: boolean
  lowVolume: boolean
}

/** El valor de la medida elegida y, si es la espera, cuántos pacientes la respaldan; después, las notas del día. */
function tooltipHtml(intl: IntlShape, day: UrgencyDailyPointDTO, measure: UrgencyMeasure, flags: PointFlags): string {
  const text = (id: string, values?: Record<string, string | number>) => escapeHtml(intl.formatMessage({ id }, values))
  const lines = [`<div class="nv-urg-tip__title">${escapeHtml(dayTitle(intl, day))}</div>`]
  if (measure === 'arrivals') {
    lines.push(`<div>${text('UrgencySummarySeriesArrivals')}: ${formatCount(intl, day.arrivals)}</div>`)
    lines.push(`<div>${text('UrgencySummarySeriesLeft')}: ${formatCount(intl, day.leftWithoutAttention)}</div>`)
  } else {
    lines.push(`<div>${text('UrgencySummarySeriesWait')}: ${escapeHtml(formatMinutes(intl, day.avgWaitMinutes))}</div>`)
    lines.push(`<div>${text('UrgencySummaryTipWaited', { count: day.waitedCount, countText: formatCount(intl, day.waitedCount) })}</div>`)
  }
  if (flags.peak) lines.push(`<div class="nv-urg-tip__note nv-urg-tip__note--peak">${text('UrgencySummaryTipPeak')}</div>`)
  if (flags.lowVolume) lines.push(`<div class="nv-urg-tip__note">${text('UrgencySummaryTipLow')}</div>`)
  lines.push(`<div class="nv-urg-tip__note">${text('UrgencySummaryTipOpen')}</div>`)
  return `<div class="nv-urg-tip">${lines.join('')}</div>`
}

/**
 * Evolución diaria: una línea con un punto por día de llegada, de las llegadas (con «Se retiraron sin atención» como
 * segunda serie, punteada y con otro marcador) o de la espera media. El eje X es un eje de tiempo real: ApexCharts ralea
 * solo los rótulos para que no se pisen. Los días sin llegadas (sin datos) no se dibujan, no se alcanzan con el teclado y
 * no abren detalle: no cuentan como cero. Una línea punteada marca el promedio del período; con la espera, los días con
 * pocos pacientes llevan un marcador hueco (su promedio puede no ser representativo) y los picos, un marcador más grande
 * con su etiqueta. Un click, un tap o Enter sobre un día abre su detalle; con el teclado, el contenedor es un grupo
 * enfocable: flechas e Inicio/Fin mueven un punto resaltado y una región `aria-live` dice dónde está (el mismo patrón
 * que la evolución de Estadísticas de atención, que está tipada a sus propios datos).
 * TODO(design-system): gráficos. Novarium no define el estilo de los gráficos.
 * TODO(design-system): color de pico en gráficos. Se usa el par --warn / --warn-bg de la etiqueta «Atención» del ranking.
 */
export default function UrgencyTimelineChart({ daily, measure, ariaLabel, average, analysis, onOpen }: UrgencyTimelineChartProps) {
  const intl = useIntl()
  const ref = useRef<HTMLDivElement>(null)
  const hintId = useId()
  const tokens = useChartTokens(ref)
  const isArrivals = measure === 'arrivals'
  /** Posición en `plot` del día resaltado con el teclado (apuntarlo con el mouse lo resuelve el tooltip). */
  const [activePlot, setActivePlot] = useState<number | null>(null)
  const lastOpenRef = useRef(0)

  /**
   * Lo que se dibuja: los días con llegadas (y, con la espera, los que tienen espera medible). Se agregan puntos sin valor
   * en los extremos del rango, para que el eje de tiempo abarque todo el rango y ApexCharts no lo achique a los puntos
   * dibujados (con un solo punto no sabe dibujar los rótulos del eje); un rango de un solo día lleva un día más a cada lado.
   */
  const plot = useMemo<PlotPoint[]>(() => {
    if (daily.length === 0) return []
    const drawn: PlotPoint[] = daily.flatMap((day, index) => {
      const y = valueOf(day, measure)
      return hasArrivals(day) && y !== null
        ? [{ x: dayStart(day).getTime(), y, left: day.leftWithoutAttention, day, index }]
        : []
    })
    const spare = daily.length === 1 ? 1 : 0
    const lowX = addDays(dayStart(daily[0]), -spare).getTime()
    const highX = addDays(dayStart(daily[daily.length - 1]), spare).getTime()
    if (drawn.length === 0 || drawn[0].x > lowX) drawn.unshift({ x: lowX, y: null, left: null, day: null, index: -1 })
    if (drawn[drawn.length - 1].x < highX) drawn.push({ x: highX, y: null, left: null, day: null, index: -1 })
    return drawn
  }, [daily, measure])

  const series = useMemo(() => [
    {
      name: intl.formatMessage({ id: isArrivals ? 'UrgencySummarySeriesArrivals' : 'UrgencySummarySeriesWait' }),
      data: plot.map(({ x, y }) => ({ x, y })),
    },
    ...(isArrivals
      ? [{ name: intl.formatMessage({ id: 'UrgencySummarySeriesLeft' }), data: plot.map(({ x, left }) => ({ x, y: left })) }]
      : []),
  ], [intl, plot, isArrivals])

  const flagsOf = useCallback((index: number): PointFlags => ({
    peak: analysis?.peaks.includes(index) ?? false,
    lowVolume: analysis?.lowVolume[index] ?? false,
  }), [analysis])

  /** Los días a los que se llega con las flechas: los dibujados. */
  const navigable = useMemo(() => plot.flatMap((item, position) => (item.day ? [position] : [])), [plot])

  useEffect(() => { setActivePlot(null) }, [plot])

  /** Abre el detalle del día en la posición `position` de `plot`. */
  const openAt = useCallback((position: number) => {
    const day = plot[position]?.day
    if (!day) return
    const now = Date.now()
    if (now - lastOpenRef.current < DUPLICATE_CLICK_MS) return
    lastOpenRef.current = now
    setActivePlot(position)
    onOpen(day, ref.current)
  }, [plot, onOpen])

  const options = useMemo<ApexOptions | null>(() => {
    if (!tokens || plot.length === 0 || !ref.current) return null
    const rangeStart = dayStart(daily[0]).getTime()
    const rangeEnd = dayStart(daily[daily.length - 1]).getTime()
    const warnBg = getComputedStyle(ref.current).getPropertyValue('--warn-bg').trim()
    const font = { fontFamily: tokens.font, fontSize: '12px' }

    // Marcadores huecos: días con pocos pacientes (solo la espera)
    const hollow = isArrivals ? [] : plot.flatMap((item, position) => (item.day && analysis?.lowVolume[item.index]
      ? [{
        seriesIndex: 0, dataPointIndex: position, fillColor: tokens.card, strokeColor: tokens.primary,
        size: MARKER_SIZE, shape: 'circle' as const,
      }] : []))

    const peakAnnotations = plot.flatMap((item) => {
      if (!item.day || item.y === null || !analysis?.peaks.includes(item.index)) return []
      return [{
        x: item.x,
        y: item.y,
        marker: { size: PEAK_MARKER_SIZE, fillColor: tokens.warn, strokeColor: warnBg, strokeWidth: 2 },
        label: {
          text: peakLabel(intl, dayPoint(dayStart(item.day), item.day.arrivals, item.day.avgWaitMinutes, null), 'DAY', item.y),
          borderColor: tokens.warn,
          borderWidth: 1,
          offsetY: -4,
          style: { ...font, color: tokens.warn, background: warnBg, fontWeight: 600 },
        },
      }]
    })

    const activeItem = activePlot === null ? null : plot[activePlot]
    const activeAnnotations = activeItem && activeItem.day && activeItem.y !== null ? [{
      x: activeItem.x,
      y: activeItem.y,
      marker: { size: ACTIVE_MARKER_SIZE, fillColor: tokens.card, strokeColor: tokens.textStrong, strokeWidth: 2 },
    }] : []

    return {
      chart: {
        type: 'line',
        height: CHART_HEIGHT,
        fontFamily: tokens.font,
        foreColor: tokens.text2,
        toolbar: { show: false },
        zoom: { enabled: false },
        animations: { enabled: false },
        events: {
          // Con el tooltip compartido ApexCharts avisa del click con el día más cercano, no hace falta acertarle al punto
          markerClick: (_event: unknown, _chart: unknown, config: { dataPointIndex: number }) => openAt(config.dataPointIndex),
          dataPointSelection: (_event: unknown, _chart: unknown, config: { dataPointIndex: number }) => openAt(config.dataPointIndex),
        },
      },
      colors: [tokens.primary, tokens.teal700],
      states: { active: { filter: { type: 'none' } } },
      stroke: { width: 2, curve: 'straight', lineCap: 'round', dashArray: isArrivals ? [0, LEFT_DASH] : [0] },
      markers: {
        size: MARKER_SIZE,
        shape: ['circle', 'square'],
        strokeWidth: 2,
        strokeColors: [tokens.primary, tokens.teal700],
        discrete: hollow,
      },
      dataLabels: { enabled: false },
      legend: {
        show: isArrivals,
        position: 'top',
        horizontalAlign: 'left',
        fontSize: '12px',
        labels: { colors: tokens.text2 },
        markers: { size: 6, strokeWidth: 0, shape: ['circle', 'square'] },
        itemMargin: { horizontal: 12 },
      },
      grid: { borderColor: tokens.divider, strokeDashArray: 0 },
      annotations: {
        yaxis: average !== null ? [{
          y: average,
          borderColor: tokens.text2,
          strokeDashArray: 4,
          label: {
            text: isArrivals
              ? intl.formatMessage({ id: 'UrgencySummaryAverageArrivals' }, { value: formatCount(intl, Math.round(average)) })
              : intl.formatMessage({ id: 'UrgencySummaryAverageWait' }, { value: formatMinutes(intl, average) }),
            position: 'left',
            textAnchor: 'start',
            offsetX: 4,
            borderWidth: 0,
            style: { ...font, color: tokens.text2, background: 'transparent' },
          },
        }] : [],
        points: [...peakAnnotations, ...activeAnnotations],
      },
      xaxis: {
        type: 'datetime',
        axisBorder: { color: tokens.divider },
        axisTicks: { color: tokens.divider },
        tooltip: { enabled: false },
        labels: {
          rotate: 0,
          datetimeUTC: false,
          hideOverlappingLabels: true,
          showDuplicates: false,
          style: { fontSize: '12px' },
          // Con un solo punto ApexCharts no pasa la marca de tiempo: el valor es la marca misma
          formatter: (value: string, timestamp?: number) => {
            const time = timestamp ?? Number(value)
            // Los días vacíos de los extremos (ver `plot`) no llevan rótulo: no son parte del rango
            if (!Number.isFinite(time) || time < rangeStart - HALF_DAY_MS || time > rangeEnd + HALF_DAY_MS) return ''
            return axisLabel(intl, time, 'DAY')
          },
        },
      },
      yaxis: {
        min: 0,
        forceNiceScale: true,
        title: {
          text: intl.formatMessage({ id: isArrivals ? 'UrgencySummaryAxisPatients' : 'UrgencySummaryAxisMinutes' }),
          style: { fontSize: '12px', fontWeight: 500, color: tokens.text2 },
        },
        labels: {
          style: { fontSize: '12px' },
          formatter: (value: number) => (isArrivals ? formatCount(intl, Math.round(value)) : `${formatCount(intl, Math.round(value))} min`),
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
          // Un punto sin valor es un extremo del rango: se muestra el día dibujado más cercano
          const item = plot[dataPointIndex]?.day
            ? plot[dataPointIndex]
            : (dataPointIndex <= 0 ? plot.find((candidate) => candidate.day) : [...plot].reverse().find((candidate) => candidate.day))
          return item?.day ? tooltipHtml(intl, item.day, measure, flagsOf(item.index)) : ''
        },
      },
    }
  }, [intl, tokens, plot, daily, isArrivals, measure, average, analysis, activePlot, openAt, flagsOf])

  // ── Teclado ──

  /** Lo que se dice (y se ve) de un día: «Sábado 19/09/2026: espera media 78,0 min, 241 pacientes con espera medida. Pico.» */
  const describe = (position: number): string => {
    const item = plot[position]
    if (!item?.day) return ''
    const period = dayTitle(intl, item.day)
    const flags = flagsOf(item.index)
    const parts = [
      isArrivals
        ? intl.formatMessage({ id: 'UrgencySummaryLiveArrivals' }, {
          period, count: item.day.arrivals, countText: formatCount(intl, item.day.arrivals), left: item.day.leftWithoutAttention,
          leftText: formatCount(intl, item.day.leftWithoutAttention),
        })
        : intl.formatMessage({ id: 'UrgencySummaryLiveWait' }, {
          period, value: formatMinutes(intl, item.day.avgWaitMinutes), count: item.day.waitedCount,
          countText: formatCount(intl, item.day.waitedCount),
        }),
      flags.peak ? intl.formatMessage({ id: 'UrgencySummaryLivePeak' }) : '',
      flags.lowVolume ? intl.formatMessage({ id: 'UrgencySummaryLiveLow' }) : '',
    ]
    return parts.filter(Boolean).join(' ')
  }

  const move = (event: KeyboardEvent<HTMLDivElement>) => {
    if (navigable.length === 0) return
    const current = activePlot === null ? -1 : navigable.indexOf(activePlot)
    let next: number | null = null
    if (event.key === 'ArrowRight') next = Math.min(navigable.length - 1, current + 1)
    else if (event.key === 'ArrowLeft') next = current < 0 ? navigable.length - 1 : Math.max(0, current - 1)
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = navigable.length - 1
    else if ((event.key === 'Enter' || event.key === ' ') && activePlot !== null) {
      event.preventDefault()
      openAt(activePlot)
      return
    }
    if (next !== null) {
      event.preventDefault()
      setActivePlot(navigable[next])
    }
  }

  const activeText = activePlot === null ? '' : describe(activePlot)

  return (
    <div className="nv-urg-timeline">
      <div
        ref={ref}
        className="nv-urg-chart nv-urg-chart--interactive"
        role="group"
        aria-label={ariaLabel}
        aria-roledescription={intl.formatMessage({ id: 'UrgencySummaryChartRole' })}
        aria-describedby={hintId}
        tabIndex={0}
        onKeyDown={move}
        onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setActivePlot(null) }}
      >
        <div aria-hidden="true">
          {options && (
            <ReactApexChart key={measure} type="line" height={CHART_HEIGHT} series={series} options={options} />
          )}
        </div>
        <span id={hintId} className="nv-sr-only">{intl.formatMessage({ id: 'UrgencySummaryChartHint' })}</span>
        <span className="nv-sr-only" role="status" aria-live="polite">{activeText}</span>
      </div>
      {/* Solo el día resaltado con el teclado; el lector de pantalla lo oye por la región `aria-live` de arriba */}
      {activeText && <p className="nv-urg-timeline__detail nv-help" aria-hidden="true">{activeText}</p>}
    </div>
  )
}
